import layerStylesheet from './withLayer.scss?inline';

import CustomComponent from '../customComponent/CustomComponent';

const LAYER_MARKER = Symbol('nxt-layer-marker');

export type AbstractConstructor<T = object> = abstract new (...args: any[]) => T;

interface LayerLike {
    [LAYER_MARKER]: true;
    readonly step: number;
    readonly layerIndex: number;
}

function isLayerLike(node: unknown): node is LayerLike & HTMLElement {
    return !!node && typeof node === 'object' && LAYER_MARKER in (node as Record<PropertyKey, unknown>);
}

function findParentLayer(element: HTMLElement): (LayerLike & HTMLElement) | null {
    let node: HTMLElement | null = element.parentElement;
    let root: Node = element.getRootNode();

    while (true) {
        while (node) {
            if (node !== element && isLayerLike(node)) {
                return node;
            }
            node = node.parentElement;
        }

        if (root instanceof ShadowRoot) {
            const host = root.host as HTMLElement;
            if (isLayerLike(host)) {
                return host;
            }
            node = host.parentElement;
            root = host.getRootNode();
        } else {
            return null;
        }
    }
}

export function withLayer<TBase extends AbstractConstructor<CustomComponent>>(Base: TBase) {
    abstract class Layer extends Base {
        [LAYER_MARKER] = true as const;
        readonly DEFAULT_LAYER_STEP: number = 1;

        private _layerIndex: number | null = null;

        constructor(...args: any[]) {
            super(...args);

            const layerStyleElement = document.createElement('style');
            layerStyleElement.textContent = layerStylesheet;
            this.shadowRoot?.appendChild(layerStyleElement);
        }

        get step(): number {
            if (!this.hasAttribute('layer-step')) {
                return this.DEFAULT_LAYER_STEP;
            }
            const parsed = parseInt(this.getAttribute('layer-step'), 10);
            if (isNaN(parsed) || parsed < 0) {
                return this.DEFAULT_LAYER_STEP;
            }
            return parsed;
        }

        get layerIndex(): number {
            if (this._layerIndex === null) {
                const parentLayer = findParentLayer(this);
                this._layerIndex = (parentLayer?.layerIndex ?? 0) + this.step;
            }
            return this._layerIndex;
        }

        connectedCallback() {
            super.connectedCallback();
            const isOdd = this.layerIndex % 2 === 1;
            this.classList.toggle('layer-odd', isOdd);
            this.classList.toggle('layer-even', !isOdd);
            this.classList.add(`layer-step-${this.step}`);
        }
    }

    return Layer;
}
